/**
 * `ink` normaliza o tamanho óptico. Os desenhos nasceram com caixas de tinta
 * bem diferentes dentro do mesmo viewBox de 24 — a engrenagem ocupa 22x22 e a
 * lista, 16x14. Lado a lado, num menu, isso lê como ícone desalinhado, mesmo
 * com todos geometricamente centrados: o olho compara a mancha, não a caixa.
 *
 * O valor é a maior dimensão da tinta do desenho; a escala leva todos para a
 * mesma altura visual. A espessura do traço é dividida pela mesma escala para
 * cancelá-la: sem isso, o ícone reduzido sairia mais fino que os outros e a
 * correção de tamanho viraria um problema de peso.
 */
function IconBase({ size = 20, ink = 18, children, ...props }) {
  const escala = 18 / ink;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8 / escala}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {escala === 1 ? children : (
        <g transform={`translate(12 12) scale(${escala}) translate(-12 -12)`}>{children}</g>
      )}
    </svg>
  );
}

export function IconGrid(props) {
  return (
    <IconBase {...props}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </IconBase>
  );
}

export function IconColumns(props) {
  return (
    <IconBase {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16M15 4v16" />
    </IconBase>
  );
}

export function IconList(props) {
  return (
    <IconBase ink={16} {...props}>
      <circle cx="5" cy="6" r="1" fill="currentColor" stroke="none" />
      <circle cx="5" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="5" cy="18" r="1" fill="currentColor" stroke="none" />
      <path d="M9 6h11M9 12h11M9 18h11" />
    </IconBase>
  );
}

export function IconSettings(props) {
  return (
    <IconBase ink={22} {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.63.29 1.3.46 2 .51H21a2 2 0 0 1 0 4h-.09c-.7.05-1.37.22-2 .51z" />
    </IconBase>
  );
}

export function IconPackage(props) {
  return (
    <IconBase {...props}>
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4a2 2 0 0 0 1-1.73z" />
      <path d="M3.27 6.96 12 12l8.73-5.04M12 22.08V12" />
    </IconBase>
  );
}

export function IconBurger(props) {
  return (
    <IconBase {...props}>
      <path d="M4 10a8 7 0 0 1 16 0Z" />
      <circle cx="9" cy="6.5" r="0.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="5" r="0.6" fill="currentColor" stroke="none" />
      <circle cx="15" cy="6.5" r="0.6" fill="currentColor" stroke="none" />
      <path d="M3 13.5c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0" />
      <path d="M3 17h18" />
      <path d="M3 17.3h18a1 1 0 0 1 1 1 2.7 2.7 0 0 1-2.7 2.7H4.7A2.7 2.7 0 0 1 2 18.3a1 1 0 0 1 1-1Z" />
    </IconBase>
  );
}

export function IconDollarSign(props) {
  return (
    <IconBase {...props}>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </IconBase>
  );
}

export function IconBell(props) {
  return (
    <IconBase ink={20} {...props}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </IconBase>
  );
}

export function IconChevronDown(props) {
  return (
    <IconBase {...props}>
      <polyline points="6 9 12 15 18 9" />
    </IconBase>
  );
}

export function IconArrowRight(props) {
  return (
    <IconBase {...props}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </IconBase>
  );
}

export function IconStar(props) {
  return (
    <IconBase {...props}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </IconBase>
  );
}

export function IconMenu(props) {
  return (
    <IconBase {...props}>
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </IconBase>
  );
}

export function IconSearch(props) {
  return (
    <IconBase {...props}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </IconBase>
  );
}

export function IconLogOut(props) {
  return (
    <IconBase {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </IconBase>
  );
}

export function IconCheckCircle(props) {
  return (
    <IconBase {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </IconBase>
  );
}

export function IconClock(props) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 16 14" />
    </IconBase>
  );
}

export function IconAlertTriangle(props) {
  return (
    <IconBase {...props}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </IconBase>
  );
}

export function IconInfo(props) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </IconBase>
  );
}

export function IconTrash(props) {
  return (
    <IconBase {...props}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </IconBase>
  );
}

export function IconPlus(props) {
  return (
    <IconBase {...props}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </IconBase>
  );
}

/** Um "+" dentro de um círculo — usado para "adicional/extra", em vez de
 *  IconPlus (que já significa "criar novo") ou de um cifrão (que sugere
 *  dinheiro, não o conceito de item opcional que pode ser grátis). */
export function IconPlusCircle(props) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </IconBase>
  );
}

export function IconTag(props) {
  return (
    <IconBase {...props}>
      <path d="M12.59 2.59 21 11l-9 9-8.41-8.41A2 2 0 0 1 3 10.17V4a2 2 0 0 1 2-2h6.17a2 2 0 0 1 1.42.59z" />
      <circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" stroke="none" />
    </IconBase>
  );
}

export function IconPencil(props) {
  return (
    <IconBase {...props}>
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" />
      <path d="M15 5l4 4" />
    </IconBase>
  );
}

export function IconMoreVertical(props) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1.4" fill="currentColor" stroke="none" />
    </IconBase>
  );
}

export function IconImage(props) {
  return (
    <IconBase {...props}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
      <polyline points="21 15 16 10 5 21" />
    </IconBase>
  );
}

export function IconMoped(props) {
  return (
    <IconBase {...props}>
      <circle cx="6" cy="17" r="2.5" />
      <circle cx="18" cy="17" r="2.5" />
      <path d="M8.5 17h7" />
      <path d="M15.5 17V8h2.5l2 4v5" />
      <path d="M6 14.5V11h4" />
    </IconBase>
  );
}

export function IconBag(props) {
  return (
    <IconBase {...props}>
      <path d="M6 8h12l-1 12H7L6 8z" />
      <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    </IconBase>
  );
}

export function IconUtensils(props) {
  return (
    <IconBase {...props}>
      <path d="M7 3v6a2.5 2.5 0 0 0 5 0V3" />
      <path d="M9.5 11.5V21" />
      <path d="M17.5 3c1.4 1.7 1.4 5.3 0 7H16V3z" />
      <path d="M16.75 10v11" />
    </IconBase>
  );
}

export function IconGrip(props) {
  return (
    <IconBase {...props}>
      <circle cx="9" cy="6" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="15" cy="6" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="9" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="9" cy="18" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="15" cy="18" r="1.4" fill="currentColor" stroke="none" />
    </IconBase>
  );
}

export function IconLink(props) {
  return (
    <IconBase {...props}>
      <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </IconBase>
  );
}

/** O balão do WhatsApp. É marca, não metáfora: um balão genérico levaria a
 *  pensar em "mensagem" ou "observação", e este botão abre um aplicativo
 *  específico — o cliente precisa reconhecer para onde vai antes de clicar. */
export function IconWhatsapp(props) {
  return (
    <IconBase ink={17} {...props}>
      <path d="M20.5 11.6a8.4 8.4 0 0 1-12.4 7.4l-4.6 1.5 1.5-4.5A8.4 8.4 0 1 1 20.5 11.6z" />
      <path d="M9.1 8.8c.2-.5.5-.6.8-.6h.5c.2 0 .4.1.5.4l.7 1.6c.1.2 0 .4-.1.6l-.4.5c-.2.2-.2.4-.1.6a5.6 5.6 0 0 0 2.5 2.2c.2.1.4.1.6-.1l.5-.5c.2-.2.4-.2.6-.1l1.6.8c.2.1.3.3.3.5 0 1-.7 1.8-1.6 1.9-.3 0-.6 0-.9-.1a10.4 10.4 0 0 1-6.2-6.2c-.2-.5-.2-1.1.2-1.5z" />
    </IconBase>
  );
}

export function IconEye(props) {
  return (
    <IconBase {...props}>
      <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12z" />
      <circle cx="12" cy="12" r="2.75" />
    </IconBase>
  );
}

export function IconEyeOff(props) {
  return (
    <IconBase {...props}>
      <path d="M10.6 6.1A9.9 9.9 0 0 1 12 5.5c6.5 0 10 6.5 10 6.5a17 17 0 0 1-3.2 4.05M6.5 7.9A17 17 0 0 0 2 12s3.5 6.5 10 6.5a9.6 9.6 0 0 0 3.7-.7" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="M3 3l18 18" />
    </IconBase>
  );
}

export function IconInbox(props) {
  return (
    <IconBase {...props}>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </IconBase>
  );
}
